import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { Baseurl } from "../../config/BaseUrl";
import {
  FaMinus,
  FaPlus,
  FaClock,
  FaTag,
  FaStar,
  FaFire,
  FaBolt,
  FaShoppingCart,
  FaTrash,
  FaChevronDown,
  FaStore,
  FaCheck,
  FaShoppingBag,
  FaEye,
} from "react-icons/fa";
import { SetLoginModalOpen } from "../../redux/authentication/AuthenticationSlice";
import { setProductBySubcat } from "../../redux/subCategory/subCategorySlice";
import SelectCarDetails from "../SelectCarDetails";
import { Delete } from "@mui/icons-material";
import {
  setChoosedCategory,
  setClickedCategory,
  setSelectedSaloon,
} from "../../redux/saloon/SaloonSlice";
import {
  addToCartBackend,
  fetchCartFromBackend,
  updateCartItemBackend,
  removeFromCartBackend,
  addToCartLocal,
  decrementCartLocal,
  clearCartBackend,
} from "../../redux/cart/CartSlice";
import toast from "react-hot-toast";

const CatBySaloon = () => {
  const { clickedSaloon, clickedCategory, choosedCategory } = useSelector(
    (store) => store.saloon
  );
  const { CartItems, netPayable, cartTotalMrp } = useSelector(
    (store) => store.Cart
  );
  const { isAuth, clientid } = useSelector((store) => store.Authentication);
  const [selectedCat, setSelectedCat] = useState(null);
  const [catBySaloon, setCatBySaloon] = useState([]);
  const [servicesByCat, setServicesByCat] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantities, setQuantities] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [selectedProductServices, setSelectedProductServices] = useState([]);
  const [selectedProductName, setSelectedProductName] = useState("");
  const [visibleServicesCount, setVisibleServicesCount] = useState(1);
  const [mobileModalVisible, setMobileModalVisible] = useState(false);
  const [selectedMobileProduct, setSelectedMobileProduct] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  // Cart quantity dropdown setup
  useEffect(() => {
    const qtyArray = Array.from({ length: 10 }, (_, i) => i + 1);
    setQuantities(qtyArray);
  }, []);

  // Authentication setup
  useEffect(() => {
    const authStatus = isAuth && !!clientid;
    setIsAuthenticated(authStatus);

    if (authStatus) {
      dispatch(fetchCartFromBackend());
    }
  }, [dispatch, isAuth, clientid]);

  // Fetch categories
  useEffect(() => {
    const getCategories = async () => {
      if (!clickedSaloon) return;

      setLoadingCategories(true);
      try {
        const url = `${Baseurl}/api/v1/saloonCategory/categorybysupercatid/${clickedSaloon}`;
        const resp = await axios.get(url);

        if (
          resp.data.success &&
          resp.data.category &&
          resp.data.category.length > 0
        ) {
          setCatBySaloon(resp.data.category);
          const firstCategory = resp.data.category[0];
          setSelectedCat(firstCategory);

          // Dispatch to Redux store
          dispatch(setChoosedCategory(firstCategory));
          if (firstCategory._id) {
            dispatch(setClickedCategory(firstCategory._id));
          }
        } else {
          // Handle empty categories
          setCatBySaloon([]);
          setSelectedCat(null);
          console.log("No categories found for this saloon");
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCatBySaloon([]);
        setSelectedCat(null);
      } finally {
        setLoadingCategories(false);
      }
    };

    getCategories();
  }, [clickedSaloon, dispatch]);

  // Fetch services
  useEffect(() => {
    const getServices = async () => {
      if (!clickedCategory) return;

      setLoadingServices(true);
      try {
        const url = `${Baseurl}/api/v1/saloonServices/servicesbycatid/${clickedCategory}`;
        const resp = await axios.get(url);

        if (resp.data.success) {
          const services = resp.data.services || [];
          let productbySubcat = services.map((product) => ({
            ...product,
            cartQuantity: 0,
          }));

          // Update cart quantities from CartItems
          CartItems.forEach((cartitem) => {
            const itemIndex = productbySubcat.findIndex(
              (product) => product._id === cartitem.ProductId
            );
            if (itemIndex >= 0) {
              productbySubcat[itemIndex].cartQuantity = cartitem.Qty;
            }
          });

          setServicesByCat(productbySubcat);
        } else {
          setServicesByCat([]);
        }
      } catch (error) {
        console.error("Error fetching services:", error);
        setServicesByCat([]);
      } finally {
        setLoadingServices(false);
      }
    };

    getServices();
  }, [CartItems, clickedCategory]);

  // Cart functions
  const deleteServiceClick = async (item) => {
    if (!isAuthenticated) {
      toast.error("Please login to manage your cart");
      navigate("/login");
      return;
    }

    try {
      await dispatch(removeFromCartBackend(item.ProductId)).unwrap();
      toast.success("Item removed from cart");
    } catch (error) {
      console.error("Failed to remove item:", error);
      toast.error("Failed to remove item from cart");
    }
  };

  const showQuantityDropdown = (item) => {
    if (!isAuthenticated) {
      toast.error("Please login to update cart");
      navigate("/login");
      return;
    }

    setSelectedItem(item);
    setDropdownVisible(true);
  };

  const updateQuantity = async (newQuantity) => {
    if (!selectedItem || !isAuthenticated) return;

    try {
      await dispatch(
        updateCartItemBackend({
          productId: selectedItem.ProductId,
          quantity: newQuantity,
        })
      ).unwrap();

      setDropdownVisible(false);
      setSelectedItem(null);
      dispatch(fetchCartFromBackend());
    } catch (error) {
      console.error("Failed to update quantity:", error);
      toast.error("Failed to update quantity");
    }
  };

  const calculateSavings = () => {
    return cartTotalMrp - netPayable;
  };

  // Category functions
  const handleSaloonChange = async (subcat) => {
    if (!subcat || !subcat._id) {
      console.error("Invalid category selected");
      return;
    }

    setSelectedCat(subcat);

    try {
      await dispatch(setClickedCategory(subcat._id));
      await dispatch(setChoosedCategory(subcat));
    } catch (error) {
      console.error("Error updating category:", error);
    }
  };

  // Services modal functions
  const handleShowMoreServices = (product) => {
    setSelectedProductServices(product.services || []);
    setSelectedProductName(product.name || product.ItemName);
    setShowServicesModal(true);
    setVisibleServicesCount(1);
  };

  const handleShowMoreInModal = () => {
    setVisibleServicesCount((prevCount) => prevCount + 2);
  };

  const handleCloseServicesModal = () => {
    setShowServicesModal(false);
    setSelectedProductServices([]);
    setSelectedProductName("");
    setVisibleServicesCount(1);
  };

  // Mobile modal functions
  const handleMobileProductPress = (product) => {
    setSelectedMobileProduct(product);
    setMobileModalVisible(true);
  };

  const handleCloseMobileModal = () => {
    setMobileModalVisible(false);
    setSelectedMobileProduct(null);
  };

  // Cart item functions
  const addToCartPress = async (product, index) => {
    try {
      if (!isAuth || !clientid) {
        dispatch(SetLoginModalOpen(true));
        return;
      }

      // Update local state first
      let products = [...servicesByCat];
      products[index].cartQuantity += 1;
      setServicesByCat(products);

      const formData = {
        salonId: product.superCategoryId,
        ProductId: product._id,
        ProductName: product.name,
        Brand: selectedCat?.name || "",
        CatName: selectedCat?.name || "",
        CatId: selectedCat?._id || "",
        Services: product.services,
        Duration: product.duration,
        ImgUrl: product.image,
        Mrp: product.mrp,
        Price: product.price,
        address: selectedCat?.address || "",
        area: selectedCat?.area || "",
        phone: selectedCat?.phone || "",
        Qty: 1,
        TotalAmount: product.price * 1,
        TotalPrice: product.price * 1,
        TotalMrp: product.mrp * 1,
        Discount: product.mrp * 1 - product.price * 1,
        productCostPrice: product.price,
        for: "Saloon",
        SalonName: clickedSaloon?.name,
      };

      dispatch(addToCartLocal(formData));
      await dispatch(addToCartBackend(formData)).unwrap();
      await dispatch(fetchCartFromBackend()).unwrap();
    } catch (error) {
      console.error("❌ Failed to add to cart:", error);
      // Revert local state on error
      let products = [...servicesByCat];
      if (products[index].cartQuantity > 0) {
        products[index].cartQuantity -= 1;
        setServicesByCat(products);
      }
    }
  };

  const decrementCartPress = async (product, index) => {
    try {
      if (!isAuth || !clientid) {
        dispatch(SetLoginModalOpen(true));
        return;
      }

      // Update local state first
      let products = [...servicesByCat];
      if (products[index].cartQuantity > 0) {
        products[index].cartQuantity -= 1;
        setServicesByCat(products);
      }

      const formData = {
        ProductId: product._id,
        ProductName: product.name,
        CatId: product.categoryId,
        CatName: product.category,
        Brand: product.brand,
        Services: product.services,
        Duration: product.duration,
        ImgUrl: product.ImgUrlMbl,
        Price: product.sellingPrice,
        Qty: 1,
        TotalAmount: product.sellingPrice * 1,
        Mrp: product.mrp,
        TotalPrice: product.sellingPrice * 1,
        TotalMrp: product.mrp * 1,
        Discount: product.mrp * 1 - product.sellingPrice * 1,
        productCostPrice: product.productCostPrice,
        SalonName: clickedSaloon?.name,
      };

      dispatch(decrementCartLocal(formData));

      const currentItem = CartItems.find(
        (item) => item.ProductId === product._id
      );

      if (currentItem && currentItem.Qty === 1) {
        await dispatch(removeFromCartBackend(product._id)).unwrap();
      } else {
        const newQuantity = currentItem ? currentItem.Qty - 1 : 0;
        if (newQuantity > 0) {
          await dispatch(
            updateCartItemBackend({
              productId: product._id,
              quantity: newQuantity,
            })
          ).unwrap();
        }
      }

      await dispatch(fetchCartFromBackend()).unwrap();
    } catch (error) {
      console.error("❌ Failed to decrement cart:", error);
    }
  };

  // Cart Item Card Component
  const CartItemCard = ({ item }) => (
    <div className="border rounded p-3 shadow-sm bg-white mb-3">
      {item.SalonName && (
        <div className="d-flex align-items-center mb-2 p-2 bg-success bg-opacity-10 rounded">
          <FaStore className="text-success me-2 small" />
          <small className="text-success fw-bold">{item.SalonName}</small>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-start mb-2">
        <h6 className="fw-bold text-dark mb-0 flex-grow-1 me-3">
          {item.ProductName}
        </h6>
        <button
          onClick={() => deleteServiceClick(item)}
          className="btn btn-link text-danger p-0 border-0"
        >
          <FaTrash className="small" />
        </button>
      </div>

      <div className="row">
        <div className="col-8">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
              <span className="h6 fw-bold text-dark mb-0">₹{item.Price}</span>
              <span className="text-muted text-decoration-line-through small">
                ₹{item.Mrp}
              </span>
              <span className="badge bg-success small">
                {(((item.Mrp - item.Price) / item.Mrp) * 100).toFixed(0)}% OFF
              </span>
            </div>
            <div className="d-flex align-items-center bg-light px-2 py-1 rounded">
              <FaClock className="text-muted me-1 small" />
              <small className="text-muted fw-semibold">{item.Duration}</small>
            </div>
          </div>

          <div className="mb-2">
            {item.Services?.slice(0, 2).map((indService, serviceIndex) => (
              <div key={serviceIndex} className="d-flex align-items-start mb-1">
                <FaCheck className="text-success me-2 mt-1 small" />
                <small className="text-muted">
                  {indService.service && indService.service.length > 50
                    ? `${indService.service.slice(0, 50)}...`
                    : indService.service}
                </small>
              </div>
            ))}
          </div>
        </div>

        <div className="col-4 text-end">
          <img
            src={item.ImgUrl}
            alt={item.ProductName}
            className="img-fluid rounded"
            style={{ width: "60px", height: "60px", objectFit: "cover" }}
          />
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center pt-2 border-top">
        <div className="d-flex align-items-center">
          <span className="text-muted me-2 small fw-semibold">Quantity:</span>
          <button
            className="btn btn-outline-secondary btn-sm d-flex align-items-center"
            onClick={() => showQuantityDropdown(item)}
          >
            <span className="me-1">{item.Qty}</span>
            <FaChevronDown className="small" />
          </button>
        </div>
        <span className="fw-bold text-dark small">
          Item Total: ₹{item.TotalAmount}
        </span>
      </div>
    </div>
  );

  // Empty Cart State
  const EmptyCartState = () => (
    <div className="border rounded p-4 shadow-sm text-center bg-white">
      <FaShoppingBag className="fs-1 text-muted" />
      <p className="mt-3 text-muted">No items in your cart</p>
    </div>
  );

  // Login Prompt
  const LoginPrompt = () => (
    <div className="border rounded p-4 shadow-sm text-center bg-white">
      <FaShoppingBag className="fs-1 text-muted" />
      <p className="mt-3 text-muted">Please login to view your cart</p>
      <button
        onClick={() => navigate("/login")}
        className="btn btn-primary w-100 mt-2"
      >
        LOGIN TO CONTINUE
      </button>
    </div>
  );

  // Cart Box Component
  const CartBox = () => {
    if (!isAuthenticated) {
      return <LoginPrompt />;
    }

    return (
      <div>
        {/* Cart Box */}
        <div className="border rounded p-4 shadow-sm text-center mb-4 bg-white">
          {CartItems.length === 0 ? (
            <EmptyCartState />
          ) : (
            <>
              <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                {CartItems.map((cart, index) => (
                  <CartItemCard key={index} item={cart} />
                ))}
              </div>

              <div className="border-top pt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted fw-semibold">
                    Subtotal ({CartItems.length}{" "}
                    {CartItems.length === 1 ? "item" : "items"})
                  </span>
                  <span className="h6 fw-bold text-dark mb-0">
                    ₹{netPayable}
                  </span>
                </div>

                {calculateSavings() > 0 && (
                  <div className="d-flex justify-content-between align-items-center mb-2 p-2 bg-success bg-opacity-10 rounded">
                    <span className="text-success fw-semibold small">
                      You save
                    </span>
                    <span className="fw-bold text-success small">
                      ₹{calculateSavings()}
                    </span>
                  </div>
                )}

                <div className="text-center text-muted small mb-3">
                  Extra charges may apply
                </div>

                {/* Coupon Section */}
                <div className="border rounded p-3 bg-white mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <FaTag className="text-success" />
                    <span>Apply Coupon</span>
                  </div>
                  <p className="small text-muted mb-1">
                    Get discounts on your booking
                  </p>
                  <p className="small fw-semibold text-primary mb-0">
                    View More Offers ▼
                  </p>
                </div>

                <button
                  onClick={() => navigate("/cart")}
                  className="btn w-100 border-0"
                  style={{ backgroundColor: "#b93c5b", color: "#fff" }}
                >
                  PROCEED TO CART - ₹{netPayable}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Quantity Dropdown Modal */}
        {dropdownVisible && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            onClick={() => setDropdownVisible(false)}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header border-bottom-0">
                  <h5 className="modal-title fw-bold">Select Quantity</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setDropdownVisible(false)}
                  ></button>
                </div>
                <div className="modal-body text-center">
                  <p className="text-muted mb-3">{selectedItem?.ProductName}</p>
                  <div
                    className="list-group"
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                  >
                    {quantities.map((quantity) => (
                      <button
                        key={quantity}
                        className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                          selectedItem?.Qty === quantity ? "active" : ""
                        }`}
                        onClick={() => updateQuantity(quantity)}
                      >
                        <span
                          className={
                            selectedItem?.Qty === quantity ? "text-white" : ""
                          }
                        >
                          {quantity}
                        </span>
                        {selectedItem?.Qty === quantity && (
                          <FaCheck className="text-white" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="modal-footer border-top-0">
                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100"
                    onClick={() => setDropdownVisible(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Footer Component
  const CartFooter = () => {
    if (!isAuthenticated || CartItems.length === 0) {
      return null;
    }

    return (
      <div className="fixed-bottom bg-white border-top shadow-lg d-lg-none">
        <div className="container-fluid">
          <div className="row align-items-center py-2">
            {/* Cart Icon and Count */}
            <div className="col-3">
              <div className="position-relative d-inline-block ms-1">
                <FaShoppingCart className="text-primary fs-4" />

                {CartItems.length > 0 && (
                  <span
                    className="position-absolute translate-middle badge rounded-pill bg-danger"
                    style={{
                      top: "-2px",
                      right: "-14px",
                      fontSize: "10px",
                      padding: "4px 6px",
                    }}
                  >
                    {CartItems.length}
                  </span>
                )}
              </div>
            </div>

            {/* Total Amount */}
            <div className="col-5">
              <div className="text-start">
                <div className="fw-bold fs-6">₹{netPayable}</div>
                <small className="text-muted">
                  {CartItems.length} {CartItems.length === 1 ? "item" : "items"}
                </small>
              </div>
            </div>

            {/* View Cart Button */}
            <div className="col-4 py-1">
              <button
                onClick={() => navigate("/cart")}
                className="btn w-100 border-0 py-1"
                style={{ backgroundColor: "#b93c5b", color: "#fff" }}
              >
                View Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="py-4 px-md-3 px-2"
      style={{ paddingBottom: CartItems.length > 0 ? "80px" : "0" }}
    >
      {/* Main Layout */}
      <div className="row mt-5">
        {/* LEFT SECTION - Categories */}
        <div className="col-xl-2.5 col-lg-3 col-md-4 col-4 mb-4">
          <div className="border rounded shadow-sm bg-white p-3">
            <h6 className="mb-3 fw-semibold">Select a service</h6>
            <hr className="mt-0" />

            {loadingCategories ? (
              <div className="text-center py-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : catBySaloon?.length > 0 ? (
              <div className="row row-cols-1 row-cols-md-3 g-3">
                {catBySaloon.map((subcat, index) => (
                  <div
                    key={index}
                    className="col text-center cursor-pointer"
                    onClick={() => handleSaloonChange(subcat)}
                  >
                    <img
                      src={subcat.desktopImage}
                      alt={subcat.name}
                      className={`img-fluid rounded ${
                        selectedCat?._id === subcat._id
                          ? "border border-primary"
                          : ""
                      }`}
                      style={{
                        width: "65px",
                        height: "65px",
                        objectFit: "cover",
                      }}
                    />
                    <p className="mt-2 small fw-medium text-truncate">
                      {subcat.name}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-3">
                <p className="text-muted">No categories available</p>
              </div>
            )}
          </div>
        </div>

        {/* CENTER SECTION - Services */}
        <div className="col-xl-6 col-lg-6 col-md-8 col-8 mb-4 d-none d-md-block">
          {loadingServices ? (
            <div className="text-left py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : servicesByCat?.length > 0 ? (
            <div className="row g-4">
              {servicesByCat.map((product, index) => (
                <div key={index} className="col-12">
                  <div className="p-4 border rounded shadow-sm bg-white h-100">
                    <div className="row align-items-start">
                      {/* Image */}
                      <div className="col-md-3 mb-3 mb-md-0">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="img-fluid rounded"
                          style={{
                            height: "150px",
                            width: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </div>

                      {/* Details */}
                      <div className="col-md-6 mb-3 mb-md-0 text-start">
                        <h5 className="fw-bold">
                          {product.ItemName || product.name}
                        </h5>

                        <div className="d-flex gap-2 mb-2">
                          <span className="badge bg-success">SERVICE</span>
                          <span className="text-muted small">
                            {product.duration}
                          </span>
                        </div>

                        <h5>
                          ₹{product.price}
                          <span className="text-decoration-line-through text-muted ms-2">
                            ₹{product.mrp}
                          </span>
                          <span className="ms-2 text-success small">
                            {Math.round(
                              ((product.mrp - product.price) / product.mrp) *
                                100
                            )}
                            % OFF
                          </span>
                        </h5>

                        <ul className="small mb-0">
                          {product.services?.slice(0, 1).map((s, i) => (
                            <li key={i}>
                              <strong>{s.service.split(":")[0]}:</strong>{" "}
                              {s.service.split(":")[1]}
                            </li>
                          ))}

                          {product.services.length > 1 && (
                            <li>
                              <button
                                onClick={() => handleShowMoreServices(product)}
                                className="btn btn-link p-0 small"
                              >
                                +{product.services.length - 1} more services
                              </button>
                            </li>
                          )}
                        </ul>
                      </div>

                      {/* Cart Controls */}
                      <div className="col-md-3">
                        <div className="d-flex flex-column align-items-center">
                          {product.cartQuantity > 0 ? (
                            <>
                              <div className="bg-light rounded-pill px-3 py-2 mb-2">
                                <div className="d-flex align-items-center gap-2">
                                  <button
                                    onClick={() =>
                                      decrementCartPress(product, index)
                                    }
                                    className="btn btn-outline-dark btn-sm rounded-circle"
                                  >
                                    <FaMinus size={12} />
                                  </button>
                                  <span className="fw-bold">
                                    {product.cartQuantity}
                                  </span>
                                  <button
                                    onClick={() =>
                                      addToCartPress(product, index)
                                    }
                                    className="btn btn-outline-dark btn-sm rounded-circle"
                                  >
                                    <FaPlus size={12} />
                                  </button>
                                </div>
                              </div>
                              <span className="fw-bold text-success">
                                ₹{product.price * product.cartQuantity}
                              </span>
                            </>
                          ) : (
                            <button
                              onClick={() => addToCartPress(product, index)}
                              className="btn btn-outline-dark w-100 rounded-pill"
                            >
                              ADD TO CART
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {product.bestSeller && (
                      <div className="bg-light rounded p-2 text-center mt-3">
                        <span className="badge bg-success">Popular Choice</span>
                        <small className="text-muted ms-2">
                          Most booked this month
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-5">
              <h4>No Services Found</h4>
            </div>
          )}
        </div>

        {/* CENTER SECTION - Services mobile*/}
        <div className="col-8 mb-3 d-block d-md-none">
          {loadingServices ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : servicesByCat?.length > 0 ? (
            <div className="row g-3">
              {servicesByCat.map((product, index) => (
                <div key={index} className="col-12">
                  <div
                    className="p-3 rounded shadow-sm bg-white"
                    onClick={() => handleMobileProductPress(product)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-start">
                      {/* Left - Image */}
                      <div className="flex-shrink-0 me-3">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="img-fluid rounded"
                          style={{
                            width: "60px",
                            height: "60px",
                            objectFit: "cover",
                          }}
                        />
                      </div>

                      {/* Right - Content */}
                      <div className="flex-grow-1 text-start">
                        <h6 className="fw-bold mb-1">
                          {product.ItemName || product.name}
                        </h6>

                        <div className="d-flex gap-2 mb-1">
                          <span className="badge bg-success small">
                            SERVICE
                          </span>
                          <span className="text-muted small">
                            {product.duration}
                          </span>
                        </div>

                        <h6 className="fw-semibold mb-1">
                          ₹{product.price}
                          <span className="text-decoration-line-through text-muted ms-2 small">
                            ₹{product.mrp}
                          </span>
                          <span className="ms-2 text-success small">
                            {Math.round(
                              ((product.mrp - product.price) / product.mrp) *
                                100
                            )}
                            % OFF
                          </span>
                        </h6>
                      </div>
                    </div>

                    {product.bestSeller && (
                      <div className="bg-light rounded p-1 text-center mt-2">
                        <span className="badge bg-success small">Popular</span>
                        <small className="text-muted ms-1 small">
                          Most booked
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <h6>No Services Found</h6>
            </div>
          )}
        </div>

        {/* RIGHT SECTION - Cart */}
        <div className="col-xl-3 col-lg-3 d-none d-lg-block">
          <CartBox />
        </div>
      </div>

      {/* Cart Footer - Mobile Only */}
      <CartFooter />

      {/* Services Modal for Desktop */}
      {showServicesModal && (
        <div
          className="modal show d-block text-start"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={handleCloseServicesModal}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg text-start"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content text-start">
              <div className="modal-header border-bottom-0">
                <h5 className="modal-title fw-bold">
                  All Services Included - {selectedProductName}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseServicesModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="list-group">
                  {selectedProductServices
                    .slice(0, visibleServicesCount)
                    .map((service, index) => (
                      <div
                        key={index}
                        className="list-group-item border-0 px-0 py-2"
                      >
                        <div className="d-flex align-items-start text-start">
                          <FaCheck className="text-success me-2 mt-1 flex-shrink-0" />
                          <div className="flex-grow-1">
                            <strong className="d-block">
                              {service.service.split(":")[0]}:
                            </strong>
                            <span className="text-muted">
                              {service.service.split(":")[1] || service.service}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>

                {/* Show More button in modal */}
                {visibleServicesCount < selectedProductServices.length && (
                  <div className="text-center mt-3">
                    <button
                      onClick={handleShowMoreInModal}
                      className="btn btn-outline-primary btn-sm"
                    >
                      Show{" "}
                      {Math.min(
                        2,
                        selectedProductServices.length - visibleServicesCount
                      )}{" "}
                      More
                    </button>
                  </div>
                )}

                {/* Show All button when close to end */}
                {visibleServicesCount >= selectedProductServices.length - 2 &&
                  visibleServicesCount < selectedProductServices.length && (
                    <div className="text-center mt-2">
                      <button
                        onClick={() =>
                          setVisibleServicesCount(
                            selectedProductServices.length
                          )
                        }
                        className="btn btn-link text-primary text-decoration-none"
                      >
                        Show All ({selectedProductServices.length})
                      </button>
                    </div>
                  )}
              </div>
              <div className="modal-footer border-top-0">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseServicesModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Product Modal */}
      {mobileModalVisible && selectedMobileProduct && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={handleCloseMobileModal}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header border-bottom-0">
                <h5 className="modal-title fw-bold">
                  {selectedMobileProduct.ItemName || selectedMobileProduct.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseMobileModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="text-left mb-3">
                  <img
                    src={selectedMobileProduct.image}
                    alt={selectedMobileProduct.name}
                    className="img-fluid rounded"
                    style={{
                      height: "200px",
                      width: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>

                <div className="d-flex gap-2 mb-2">
                  <span className="badge bg-success">SERVICE</span>
                  <span className="text-muted small">
                    {selectedMobileProduct.duration}
                  </span>
                </div>

                <h4 className="mb-3 text-start">
                  ₹{selectedMobileProduct.price}
                  <span className="text-decoration-line-through text-muted ms-2">
                    ₹{selectedMobileProduct.mrp}
                  </span>
                  <span className="ms-2 text-success small">
                    {Math.round(
                      ((selectedMobileProduct.mrp -
                        selectedMobileProduct.price) /
                        selectedMobileProduct.mrp) *
                        100
                    )}
                    % OFF
                  </span>
                </h4>

                <div className="mb-3 text-start">
                  <h6 className="fw-bold">Services Included:</h6>
                  <ul className="small">
                    {selectedMobileProduct.services?.map((service, index) => (
                      <li key={index} className="mb-2">
                        <strong>{service.service.split(":")[0]}:</strong>{" "}
                        {service.service.split(":")[1] || service.service}
                      </li>
                    ))}
                  </ul>
                </div>

                {selectedMobileProduct.bestSeller && (
                  <div className="bg-light rounded p-2 text-center mb-3">
                    <span className="badge bg-success">Popular Choice</span>
                    <small className="text-muted ms-2">
                      Most booked this month
                    </small>
                  </div>
                )}
              </div>
              <div className="modal-footer border-top-0">
                <div className="w-100">
                  {selectedMobileProduct.cartQuantity > 0 ? (
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="bg-light rounded-pill px-3 py-2">
                        <div className="d-flex align-items-center gap-3">
                          <button
                            onClick={() => {
                              const index = servicesByCat.findIndex(
                                (item) => item._id === selectedMobileProduct._id
                              );
                              if (index !== -1) {
                                decrementCartPress(
                                  selectedMobileProduct,
                                  index
                                );
                              }
                            }}
                            className="btn btn-outline-dark btn-sm rounded-circle"
                          >
                            <FaMinus size={12} />
                          </button>
                          <span className="fw-bold">
                            {selectedMobileProduct.cartQuantity}
                          </span>
                          <button
                            onClick={() => {
                              const index = servicesByCat.findIndex(
                                (item) => item._id === selectedMobileProduct._id
                              );
                              if (index !== -1) {
                                addToCartPress(selectedMobileProduct, index);
                              }
                            }}
                            className="btn btn-outline-dark btn-sm rounded-circle"
                          >
                            <FaPlus size={12} />
                          </button>
                        </div>
                      </div>
                      <span className="fw-bold text-success">
                        ₹
                        {selectedMobileProduct.price *
                          selectedMobileProduct.cartQuantity}
                      </span>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        const index = servicesByCat.findIndex(
                          (item) => item._id === selectedMobileProduct._id
                        );
                        if (index !== -1) {
                          addToCartPress(selectedMobileProduct, index);
                          handleCloseMobileModal();
                        }
                      }}
                      className="btn btn-outline-dark w-100 rounded-pill py-2"
                    >
                      ADD TO CART
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatBySaloon;
